/* Hallmark · component: audit-activity-widget · genre: modern-minimal · theme: project system
 * states: default · loading · empty · unavailable — hover/focus on range buttons and audit link
 * pre-emit critique: P4 H4 E4 S4 R5 V4
 */

/**
 * AuditActivityWidget — the dashboard's "how is the system working" chart.
 *
 * Per-day audit-event volume (success/failure/other stacked) in the same
 * visual language as the usage chart, fed by `audit.getActivityStats` via
 * the log-audit plugin's `useAuditActivity` hook. Honest degradation: until
 * the published backend ships the procedure, the hook reports unavailable
 * and the widget says so instead of faking data. The audit log page holds
 * the per-event detail — linked below.
 *
 * Calm colours (2026-10-13): success is the accent, "other" grey, and failure
 * the ONE muted warning hue (`--chart-attention`) — no full-strength
 * green/red pair. The failed count is put in context as a share of all events
 * and links straight into the audit log filtered to failures
 * (`/log-audit?status=FAILURE`, parsed by the log-audit plugin's
 * `query-filters.ts`).
 */

import {
  type AuditActivityDay,
  useAuditActivity,
} from '@xhubio-saas/core-frontend/plugin-log-audit'
import { UsageChart, type UsageHistoryEntry } from '@xhubio-saas/core-frontend/plugin-usage'
import { cn } from '@xhubio-saas/core-frontend/ui-components'
import { ArrowRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CONTROL_TRANSITION, FIELD_RING } from '../../auth/authUi'
import { RANGE_PRESETS, type RangeDays, SEGMENT_BUTTON } from './dashboardUi'

export function AuditActivityWidget() {
  const { t } = useTranslation()
  const [days, setDays] = useState<RangeDays>(30)
  const { days: activity, isLoading, isAvailable } = useAuditActivity(days)

  // Map the per-day stats into UsageChart's entry shape so the audit chart
  // and the usage chart share one visual language (stacked SVG bars).
  const { chartData, totals, seriesColors } = useMemo(() => {
    const successLabel = t('dashboard.activity.success', 'Erfolgreich')
    const failureLabel = t('dashboard.activity.failure', 'Fehlgeschlagen')
    const otherLabel = t('dashboard.activity.other', 'Übrige')
    // Status-Farben, nicht Serienfarben: Erfolg/Fehler TRAGEN Bedeutung. Nur
    // der Fehler darf auffallen — Erfolg ist der Normalfall und trägt eine
    // gedämpfte Akzentstufe (index.css, `.dashboard-calm`), „Übrige" ist grau.
    const seriesColors: Record<string, string> = {
      [successLabel]: 'var(--chart-calm-success, var(--primary))',
      [failureLabel]: 'var(--chart-attention)',
      [otherLabel]: 'var(--chart-other)',
    }
    const chartData: UsageHistoryEntry[] = activity.map((day: AuditActivityDay) => {
      const total = day.success + day.failure + day.other
      const breakdown: Record<string, number> = {}
      if (day.success > 0) breakdown[successLabel] = day.success
      if (day.failure > 0) breakdown[failureLabel] = day.failure
      if (day.other > 0) breakdown[otherLabel] = day.other
      return { date: new Date(`${day.date}T00:00:00Z`), pattern: 'total', count: total, breakdown }
    })
    const totals = activity.reduce(
      (acc, day) => {
        acc.total += day.success + day.failure + day.other
        acc.failure += day.failure
        return acc
      },
      { total: 0, failure: 0 }
    )
    return { chartData, totals, seriesColors }
  }, [activity, t])

  const numberFormat = useMemo(() => new Intl.NumberFormat(), [])
  const percentFormat = useMemo(
    () => new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }),
    []
  )
  const { startDate, endDate } = useMemo(() => {
    const end = new Date()
    const start = new Date(end)
    start.setDate(start.getDate() - days)
    return { startDate: start, endDate: end }
  }, [days])

  return (
    <div className="flex flex-col gap-6" data-testid="audit-activity-widget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('dashboard.activity.subtitle', 'Audit-Ereignisse pro Tag')}
        </p>
        <fieldset className="flex rounded-lg border border-border p-0.5">
          <legend className="sr-only">{t('dashboard.range.label', 'Zeitraum')}</legend>
          {RANGE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setDays(preset)}
              aria-pressed={days === preset}
              data-testid={`activity-range-${preset}`}
              className={SEGMENT_BUTTON(days === preset)}
            >
              {t(`dashboard.range.days${preset}`, `${preset} Tage`)}
            </button>
          ))}
        </fieldset>
      </div>

      {isLoading ? (
        <div
          className="h-56 animate-pulse rounded-md bg-muted"
          aria-hidden
          data-testid="activity-loading"
        />
      ) : !isAvailable ? (
        <p className="py-8 text-sm text-muted-foreground" data-testid="activity-unavailable">
          {t(
            'dashboard.activity.unavailable',
            'Aktivitätsdaten sind noch nicht verfügbar — der Audit-Log selbst funktioniert unverändert.'
          )}
        </p>
      ) : (
        <>
          {totals.total > 0 && (
            <div className="flex flex-wrap gap-x-8 gap-y-3" data-testid="activity-totals">
              <div>
                <p className="text-2xl font-semibold tabular-nums">
                  {numberFormat.format(totals.total)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.activity.totalEvents', 'Ereignisse gesamt')}
                </p>
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">
                  {numberFormat.format(totals.failure)}
                </p>
                <p className="text-sm text-muted-foreground" data-testid="activity-failure-share">
                  {t('dashboard.activity.failedShare', {
                    defaultValue: 'Fehlgeschlagen · {{share}} aller Ereignisse',
                    share: percentFormat.format(totals.failure / totals.total),
                  })}
                </p>
                {totals.failure > 0 && (
                  <Link
                    to="/log-audit?status=FAILURE"
                    data-testid="activity-failures-link"
                    className={cn(
                      'mt-1 inline-flex items-center gap-1 rounded-sm text-sm text-primary hover:text-[var(--primary-hover)]',
                      CONTROL_TRANSITION,
                      FIELD_RING
                    )}
                  >
                    {t('dashboard.activity.toFailures', 'Fehlgeschlagene ansehen')}
                    <ArrowRight aria-hidden className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          )}
          {/* All-zero range → hand the chart an empty array so its noDataText
              shows instead of a row of invisible 0-height bars. */}
          <UsageChart
            data={totals.total === 0 ? [] : chartData}
            seriesColors={seriesColors}
            height={220}
            granularity="day"
            startDate={startDate}
            endDate={endDate}
            noDataText={t(
              'dashboard.activity.empty',
              'Noch keine Ereignisse im Zeitraum — Systemaktivität erscheint hier.'
            )}
            totalLabel={t('dashboard.chartTotal', 'Gesamt')}
            className={totals.total === 0 ? 'py-10' : undefined}
          />
        </>
      )}

      {/* Per-event detail lives on the audit page — link instead of duplicating */}
      <div className="flex">
        <Link
          to="/log-audit"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-sm text-sm text-primary hover:text-[var(--primary-hover)]',
            CONTROL_TRANSITION,
            FIELD_RING
          )}
        >
          {t('dashboard.activity.toAudit', 'Zum Audit-Log')}
          <ArrowRight aria-hidden className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
